/**
 * data.ts — shared synthetic data for the five metrics sections.
 *
 * Deterministic (seeded PRNG, no Math.random) so the same dataset loads every
 * time the page opens: the figures in the companion essay match what the
 * reader sees, and there is no flicker between page loads.
 *
 * Two datasets, generated once at module load:
 *
 *   - `regressionPoints` — N points around y = 0.8·x + 2 with Gaussian noise.
 *     Used by Section 1 (R²) and Section 2 (MSE/MAE). The Section 1 noise
 *     slider does NOT regenerate these — it blends the clean signal with the
 *     noise via a 0..1 dial, which keeps the underlying cloud stable while the
 *     user explores how R² responds to spread.
 *
 *   - `classificationScores` — two arrays of decision scores (one for the
 *     negative class, one for the positive class), drawn from two Gaussians.
 *     Used by Sections 3 (Accuracy), 4 (Precision/Recall/F1), and 5 (ROC/AUC).
 *     The Section 5 "separation" slider shifts the positive class's mean — that
 *     is done in roc.ts by offsetting at draw time, not by mutating this array.
 */

// --- Deterministic PRNG (mulberry32) --------------------------------------
// Tiny, fast, seedable. Same seed → same sequence on every browser.
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Box–Muller transform: turn two uniforms into one standard-normal sample.
function gaussian(rng: () => number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// --- Regression dataset ----------------------------------------------------
export interface Point {
  x: number;
  y: number;
}

// The true line the points scatter around. Sections 1 and 2 both fit the same
// best-fit line to the same cloud, so these constants are the source of truth.
export const REG_SLOPE = 0.8;
export const REG_INTERCEPT = 2;
// Baseline noise sigma baked into the cloud. The Section 1 slider scales this
// from 0 (perfect line, R² = 1) through the baseline (R² ≈ 0.85 — a clearly
// positive but visibly noisy fit) up to ~3× (heavy scatter, R² → 0). Tuned so
// the default position reads as "a decent regression," not "barely better
// than the mean."
const REG_NOISE_SIGMA = 0.7;

const REG_N = 30;
const REG_SEED = 20260616; // post's date — stable across rebuilds

/**
 * Build the regression cloud. `noiseScale` multiplies the per-point noise:
 *   0 → points lie exactly on the line (R² = 1)
 *   1 → the baseline cloud
 *   2..3 → heavy scatter (R² drops toward 0)
 *
 * Regenerated each call (cheap), so Section 1 can sweep noise without caching
 * stale state.
 */
export function regressionPoints(noiseScale = 1): Point[] {
  const rng = mulberry32(REG_SEED);
  const pts: Point[] = [];
  for (let i = 0; i < REG_N; i++) {
    // x spread evenly across [0.5, 9.5].
    const x = 0.5 + (i / (REG_N - 1)) * 9;
    const noise = gaussian(rng) * REG_NOISE_SIGMA * noiseScale;
    pts.push({ x, y: REG_SLOPE * x + REG_INTERCEPT + noise });
  }
  return pts;
}

/**
 * Ordinary least squares for an array of points. Returns slope, intercept,
 * and the fitted value at the cloud's mean x. Sections 1 and 2 share this so
 * their best-fit lines always agree.
 */
export function ols(pts: Point[]): { slope: number; intercept: number } {
  const n = pts.length;
  if (n === 0) return { slope: 0, intercept: 0 };
  const meanX = pts.reduce((s, p) => s + p.x, 0) / n;
  const meanY = pts.reduce((s, p) => s + p.y, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of pts) {
    num += (p.x - meanX) * (p.y - meanY);
    den += (p.x - meanX) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  return { slope, intercept };
}

// --- Classification dataset ------------------------------------------------
// Two Gaussian "decision score" distributions. Negatives are centered at
// NEG_MEAN, positives at POS_MEAN. The Section 5 separation slider moves the
// positives' effective mean at draw time; the base arrays stay fixed.
const NEG_MEAN = 0.40;
const POS_MEAN = 0.62;
const CLASS_SIGMA = 0.12;
const CLASS_N = 60; // per class
const CLASS_SEED = 20260617;

export interface ClassSample {
  score: number; // model's decision score, normalized to ~[0, 1]
  label: 0 | 1; // ground truth
}

/**
 * Build the score arrays. `positiveShift` (used by Section 5) offsets the
 * positive class's mean — sliding the two distributions apart or together so
 * the reader can watch AUC climb or collapse. The same seeds feed both calls
 * so shifting does not reshuffle who-is-who; only the positive scores move.
 */
export function classificationScores(positiveShift = 0): ClassSample[] {
  const rngNeg = mulberry32(CLASS_SEED);
  const rngPos = mulberry32(CLASS_SEED + 1);
  const out: ClassSample[] = [];
  for (let i = 0; i < CLASS_N; i++) {
    const ns = NEG_MEAN + gaussian(rngNeg) * CLASS_SIGMA;
    out.push({ score: ns, label: 0 });
  }
  for (let i = 0; i < CLASS_N; i++) {
    const ps = POS_MEAN + positiveShift + gaussian(rngPos) * CLASS_SIGMA;
    out.push({ score: ps, label: 1 });
  }
  return out;
}

// --- Confusion-matrix accounting -------------------------------------------
// Shared by Sections 3, 4, and 5: given scores, labels, and a threshold,
// count TP / TN / FP / FN. A sample is predicted positive iff score >= thr.
export interface Confusion {
  tp: number;
  tn: number;
  fp: number;
  fn: number;
}

export function confusion(samples: ClassSample[], threshold: number): Confusion {
  let tp = 0;
  let tn = 0;
  let fp = 0;
  let fn = 0;
  for (const s of samples) {
    const predictedPositive = s.score >= threshold;
    if (predictedPositive && s.label === 1) tp++;
    else if (predictedPositive && s.label === 0) fp++;
    else if (!predictedPositive && s.label === 0) tn++;
    else fn++;
  }
  return { tp, tn, fp, fn };
}

// Trap-zero division: return 0 when the denominator is 0 (e.g. recall when
// there are no positives in the slice). This is sklearn's convention.
export function safeDiv(num: number, den: number): number {
  return den === 0 ? 0 : num / den;
}
